/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { IntlShape } from 'react-intl';
import { SoftwareQuality } from '~shared/types/clean-code-taxonomy';
import { IssueType } from '~sq-server-commons/types/issues';
import {
  SandboxSettingsBase,
  SandboxSettingsIssueTypes,
  SandboxSettingsSoftwareQualities,
} from './types';

/**
 * Check if any critical types (Security, Reliability, Bug, Vulnerability) have been added
 */
export function hasAddedCriticalTypes(
  currentQualities: SandboxSettingsSoftwareQualities,
  originalQualities: SandboxSettingsSoftwareQualities | null | undefined,
  currentTypes: SandboxSettingsIssueTypes,
  originalTypes: SandboxSettingsIssueTypes | null | undefined,
): boolean {
  // Check critical qualities
  const originalQualitySet = new Set(originalQualities?.map((item) => item.softwareQuality) || []);
  const hasAddedCriticalQualities = currentQualities.some(
    (item) =>
      !originalQualitySet.has(item.softwareQuality) &&
      [SoftwareQuality.Security, SoftwareQuality.Reliability].includes(item.softwareQuality),
  );

  // Check critical issue types
  const originalTypeSet = new Set(originalTypes?.map((item) => item.type) || []);
  const hasAddedCriticalIssueTypes = currentTypes.some(
    (item) =>
      !originalTypeSet.has(item.type) &&
      [IssueType.Bug, IssueType.Vulnerability].includes(item.type),
  );

  return hasAddedCriticalQualities || hasAddedCriticalIssueTypes;
}

export function mapQualitiesAndSeveritiesToLabels(
  intl: IntlShape,
  settings?: SandboxSettingsBase,
  isStandardExperience?: boolean,
) {
  return isStandardExperience
    ? settings?.types?.map((t) => ({
        label: intl.formatMessage({ id: `issue.type.${t.type}` }),
        severities: t.severities.map((s) => intl.formatMessage({ id: `severity.${s}` })),
      }))
    : settings?.softwareQualities?.map((q) => ({
        label: intl.formatMessage({ id: `software_quality.${q.softwareQuality}` }),
        severities: q.impactSeverities.map((s) =>
          intl.formatMessage({ id: `severity_impact.${s}` }),
        ),
      }));
}
